import React from "react";
import { NavigateFunction } from "react-router-dom";
import { ymydApi, praiApi } from "services/ymydApi";
import { ConfirmManager } from "components/organisms/CommonUI/ConfirmPopup/ConfirmManager";
import { WaitingManager } from "components/organisms/CommonUI/WaitingPopup/WaitingManager";
import Button from "components/atoms/Button";
import { COLOR_SYSTEM } from "components/design/design-system";
import { log, LogLevel } from "utils/LogUtil";

/**
 * 약문약답 회원 카카오 로그인 → PhAI 회원가입 검증 흐름
 *
 * Login/index.tsx의 인라인 onClick 흐름(L200~704)을 추출.
 * KakaoCallback에서 호출되며, 카카오 인가코드 교환 후 받은 kakaoId로 약문약답 검증을 수행.
 *
 * 분기:
 * - 약문약답 미가입 → showNotRegisteredModal
 * - level < 3 (약사 인증 대기) → showPendingApprovalModal
 * - workType === 9 (약대생) → showStudentBlockedModal
 * - 이미 PhAI 가입 → showAlreadyJoinedModal
 * - 정상 → /signup/ymyd 로 navigate
 */
export async function runYmydSignupFlow(
  kakaoId: string,
  navigate: NavigateFunction,
  // intent는 향후 분기 확장 대비 (현재는 'signup-ymyd' 단일 흐름)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  intent: string | null
): Promise<void> {
  const ymydLoginResponse = await ymydApi.loginWithKakao(kakaoId);

  // 토큰이 없는 경우 = 약문약답 미가입
  if (!ymydLoginResponse.data?.token) {
    WaitingManager.hide();
    showNotRegisteredModal();
    return;
  }

  if (ymydLoginResponse.error === 0 && ymydLoginResponse.data.token) {
    const ymydUserResponse = await ymydApi.getUserInfo(
      ymydLoginResponse.data.token
    );

    // 사용자 정보를 가져올 수 없는 경우 = 약문약답 미가입 또는 오류
    if (ymydUserResponse.error !== 0) {
      WaitingManager.hide();
      showNotRegisteredModal();
      return;
    }

    // 레벨 체크 - level이 3 미만인 경우 약사 인증 승인 대기
    if (ymydUserResponse.data.level < 3) {
      WaitingManager.hide();
      showPendingApprovalModal();
      return;
    }

    // workType 체크 - 9인 경우 대학생
    if (ymydUserResponse.data.workType === 9) {
      WaitingManager.hide();
      showStudentBlockedModal();
      return;
    }

    // 중복 가입 체크
    const checkResult = await praiApi.checkUserExists(
      ymydUserResponse.data.email
    );

    if (checkResult.exists) {
      WaitingManager.hide();
      showAlreadyJoinedModal();
    } else {
      WaitingManager.hide();
      // 회원가입 페이지로 이동 (사용자 정보 전달)
      navigate("/signup/ymyd", {
        state: { ymydUser: ymydUserResponse.data },
      });
    }
  } else {
    throw new Error("약문약답 로그인에 실패했습니다.");
  }
}

// ============================================================================
// 모달 함수 4종 — Login/index.tsx에서 그대로 추출
// ============================================================================

/**
 * 약문약답 가입 안내 모달 (1단계/2단계 박스)
 */
export function showNotRegisteredModal(): void {
  ConfirmManager.show({
    title: "약문약답 가입 안내",
    body: (
      <div style={{ textAlign: "left", lineHeight: "1.6" }}>
        <p style={{ marginBottom: "16px", fontSize: "15px" }}>
          약문약답에 가입되어 있지 않습니다
        </p>

        <div
          style={{
            backgroundColor: COLOR_SYSTEM.get("Gray")[10],
            borderRadius: "8px",
            padding: "16px",
            marginBottom: "16px",
          }}
        >
          {/* 1단계 */}
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              marginBottom: "16px",
            }}
          >
            <div
              style={{
                backgroundColor: COLOR_SYSTEM.get("Blue")[400],
                color: "white",
                borderRadius: "26%",
                width: "36px",
                height: "25px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                marginRight: "12px",
                fontSize: "12px",
                fontWeight: "bold",
              }}
            >
              1단계
            </div>
            <div>
              <p
                style={{
                  margin: 0,
                  fontSize: "14px",
                  color: COLOR_SYSTEM.get("Gray")[700],
                  fontWeight: "500",
                }}
              >
                약문약답 가입
              </p>
              <p
                style={{
                  margin: "2px 0 8px 0",
                  fontSize: "13px",
                  color: COLOR_SYSTEM.get("Gray")[500],
                }}
              >
                아래 버튼을 눌러 약문약답을 가입하고 승인을 기다려주세요
              </p>
              <p
                style={{
                  margin: "0",
                  fontSize: "12px",
                  color: COLOR_SYSTEM.get("Orange")[500],
                }}
              >
                <strong>준비물:</strong> 약사 면허증 사진
              </p>
            </div>
          </div>

          {/* 2단계 */}
          <div style={{ display: "flex", alignItems: "flex-start" }}>
            <div
              style={{
                backgroundColor: COLOR_SYSTEM.get("Blue")[400],
                color: "white",
                borderRadius: "26%",
                width: "36px",
                height: "25px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                marginRight: "12px",
                fontSize: "12px",
                fontWeight: "bold",
              }}
            >
              2단계
            </div>
            <div>
              <p
                style={{
                  margin: 0,
                  fontSize: "14px",
                  color: COLOR_SYSTEM.get("Gray")[700],
                  fontWeight: "500",
                }}
              >
                PhAI 회원가입
              </p>
              <p
                style={{
                  margin: "2px 0 0 0",
                  fontSize: "13px",
                  color: COLOR_SYSTEM.get("Gray")[500],
                }}
              >
                약문약답 회원가입이 승인되고 나면
                <br />
                첫 화면으로 돌아가서 PhAI 회원가입을 완료해주세요.
              </p>
            </div>
          </div>
        </div>
      </div>
    ),
    buttons: [
      <Button
        key="go-to-ymyd"
        type="Text"
        size="Medium"
        variant="Contained"
        color="Primary"
        style={{ width: "100%" }}
        onClick={() => {
          window.open("https://app.stg.ymyd.onjourney.co.kr/", "_blank");
          ConfirmManager.hide();
        }}
      >
        약문약답 가입하기
      </Button>,
      <Button
        key="close"
        type="Text"
        size="Medium"
        variant="Outlined"
        color="Secondary"
        style={{ width: "100%" }}
        onClick={() => {
          ConfirmManager.hide();
        }}
      >
        닫기
      </Button>,
    ],
  });
}

/**
 * 약사 인증 대기 중 모달
 */
export function showPendingApprovalModal(): void {
  ConfirmManager.show({
    title: "약사 인증 대기 중",
    body: (
      <div style={{ textAlign: "left", lineHeight: "1.6" }}>
        <div
          style={{
            marginBottom: "20px",
            textAlign: "center",
            paddingBottom: "20px",
            borderBottom: `1px solid ${COLOR_SYSTEM.get("Gray")[50]}`,
          }}
        >
          <div style={{ fontSize: "48px", marginBottom: "12px" }}>⏱️</div>
          <p
            style={{
              fontSize: "16px",
              fontWeight: "600",
              color: COLOR_SYSTEM.get("Gray")[900],
              marginBottom: "8px",
            }}
          >
            약사 면허 인증 승인 대기 중입니다
          </p>
          <p
            style={{
              fontSize: "14px",
              color: COLOR_SYSTEM.get("Gray")[500],
              marginBottom: "16px",
            }}
          >
            📌 승인 완료 후 PhAI 회원가입을 진행해주세요
          </p>
          <p
            style={{
              fontSize: "14px",
              color: COLOR_SYSTEM.get("Gray")[500],
              textAlign: "center",
            }}
          >
            평일 당일 처리 · 주말/공휴일 다음 영업일 처리
          </p>
        </div>
      </div>
    ),
    buttons: [
      <Button
        key="ok"
        type="Text"
        size="Medium"
        variant="Contained"
        color="Primary"
        style={{ width: "100%" }}
        onClick={() => ConfirmManager.hide()}
      >
        확인
      </Button>,
    ],
  });
}

/**
 * 약대생 가입 불가 모달
 */
export function showStudentBlockedModal(): void {
  ConfirmManager.show({
    title: "가입 불가",
    body: "약대생은 PhAI 서비스를 이용할 수 없습니다",
    buttons: [
      <Button
        key="ok"
        type="Text"
        size="Medium"
        variant="Contained"
        color="Primary"
        style={{ width: "100%" }}
        onClick={() => ConfirmManager.hide()}
      >
        확인
      </Button>,
    ],
  });
}

/**
 * 이미 PhAI 가입된 계정 모달
 */
export function showAlreadyJoinedModal(): void {
  ConfirmManager.show({
    title: "이미 가입됨",
    body: "이미 PhAI에 가입된 계정입니다",
    buttons: [
      <Button
        key="ok"
        type="Text"
        size="Medium"
        variant="Contained"
        color="Primary"
        style={{ width: "100%" }}
        onClick={() => ConfirmManager.hide()}
      >
        로그인 페이지로 이동
      </Button>,
    ],
  });
}
